import React, { useEffect, useMemo, useState } from "react";

export default function SectorCheckIn({ token, user, sectorId }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [data, setData] = useState({ sector: null, schedule: null, checkin_open: false, units: [], stats: null, members: [] });
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [filterChecked, setFilterChecked] = useState("all");

  useEffect(() => {
    const handle = setTimeout(() => setDebouncedSearch(search.trim().toLowerCase()), 400);
    return () => clearTimeout(handle);
  }, [search]);

  useEffect(() => {
    let active = true;
    const fetchData = async () => {
      setLoading(true);
      setError("");
      try {
        const url = new URL("https://smartcoreapi.ssfkerala.org/sectors/checkin.php");
        if (sectorId) {
          url.searchParams.set("sector_id", sectorId);
        }
        const res = await fetch(url.toString(), {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!res.ok) {
          throw new Error("Failed to load sector check-in data");
        }
        const json = await res.json();
        if (!active) return;
        setData({
          sector: json.sector ?? null,
          schedule: json.schedule ?? null,
          checkin_open: Boolean(json.checkin_open),
          units: json.units ?? [],
          stats: json.stats ?? null,
          members: json.members ?? []
        });
      } catch (err) {
        if (!active) return;
        setError(err.message || "Failed to load sector check-in data");
        setData({ sector: null, schedule: null, checkin_open: false, units: [], stats: null, members: [] });
      } finally {
        if (active) setLoading(false);
      }
    };

    fetchData();
    return () => {
      active = false;
    };
  }, [token, sectorId]);

  const filteredMembers = useMemo(() => {
    const candidates = data.members.filter((member) => {
      if (filterChecked === "checked_in" && !member.checked_in) return false;
      if (filterChecked === "pending" && member.checked_in) return false;
      return true;
    });
    if (!debouncedSearch) return candidates;
    const term = debouncedSearch;
    return candidates.filter((member) => {
      const haystack = `${member.name ?? ""} ${member.phone ?? ""} ${member.unit_name ?? ""}`.toLowerCase();
      return haystack.includes(term);
    });
  }, [data.members, debouncedSearch, filterChecked]);

  const summary = useMemo(() => {
    const total = data.stats?.total_members ?? data.members.length;
    const checkedStat = data.stats?.checked_in;
    const checked = typeof checkedStat === "number" ? checkedStat : data.members.filter((member) => member.checked_in).length;
    return { total, checked };
  }, [data.stats, data.members]);

  const sectorName = data.sector?.orgname ?? user.orgname;
  const programDate = data.schedule?.program_date ?? "Not set";

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div className="space-y-1">
          <h2 className="text-xl font-semibold text-gray-900">Sector Check-In Overview</h2>
          <p className="text-sm text-gray-500">{sectorName}</p>
          <div className="text-xs text-gray-500">{`Program date: ${programDate}`}</div>
          <div
            className={`text-xs font-semibold ${data.checkin_open ? "text-green-600" : "text-orange-600"}`}
          >
            {data.checkin_open ? "Division check-in is open" : "Awaiting division check-in window"}
          </div>
        </div>
        <div className="flex flex-col gap-2 md:w-80">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search members"
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
          <select
            value={filterChecked}
            onChange={(e) => setFilterChecked(e.target.value)}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            <option value="all">All members</option>
            <option value="checked_in">Checked-in</option>
            <option value="pending">Pending</option>
          </select>
        </div>
      </div>

      {data.stats && (
        <div className="grid grid-cols-2 gap-3 text-sm text-gray-600 sm:grid-cols-3">
          <div>
            <div className="text-xs uppercase text-gray-400">Total members</div>
            <div className="text-base font-semibold">{summary.total}</div>
          </div>
          <div>
            <div className="text-xs uppercase text-gray-400">Checked-in</div>
            <div className="text-base font-semibold text-green-600">{summary.checked}</div>
          </div>
          <div>
            <div className="text-xs uppercase text-gray-400">Units</div>
            <div className="text-base font-semibold text-indigo-600">{data.units.length}</div>
          </div>
        </div>
      )}

      {error && <div className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-600">{error}</div>}

      {loading ? (
        <div className="bg-white py-12 text-center shadow-sm">
          <div className="mx-auto h-8 w-8 animate-spin rounded-full border-4 border-blue-500 border-t-transparent"></div>
        </div>
      ) : filteredMembers.length === 0 ? (
        <div className="rounded-xl border border-dashed border-gray-200 bg-white py-12 text-center text-gray-500">
          No members match your filters.
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {filteredMembers.map((member) => {
            const checked = !!member.checked_in;
            return (
              <div key={member.id} className="rounded-2xl border p-4 shadow-sm bg-white space-y-2">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-lg font-semibold text-gray-900">{member.name}</div>
                    <div className="text-xs text-gray-500">{member.phone || "No phone"}</div>
                  </div>
                  <span
                    className={`rounded-full px-3 py-1 text-xs font-semibold ${
                      checked ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600"
                    }`}
                  >
                    {checked ? "Checked in" : "Pending"}
                  </span>
                </div>
                <div className="text-sm text-gray-600 space-y-1">
                  <div>{`Unit: ${member.unit_name || "-"}`}</div>
                  <div>{`Checked in at: ${member.checked_in_at || "-"}`}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
